import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ChevronLeft, MapPin, Zap, Droplets, Truck, Users, Instagram,
  Star, Mail, Phone, Calendar, FileCheck, CheckCircle2, XCircle,
  TrendingUp, Video, Globe, MessageSquare, LayoutGrid, FileText,
  BadgeCheck, Play
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../lib/firebase';
import { doc, getDoc, collection, query, where, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { RatingBadge } from '../components/RatingBadge';

// ─── Labels ──────────────────────────────────────────────────────────────────
const STAND_TYPE_LABELS: Record<string, string> = {
  food:        'Food Truck / Restauration Chaude',
  snacking:    'Snacking / Restauration Froide',
  craft:       'Artisanat / Créateurs',
  fashion:     'Mode / Friperie / Vêtements',
  vinyls:      'Vinyles',
  tattoo:      'Tatouage / Piercing / Paillettes',
  associative: 'Engagé / Associatif',
  partners:    'Partenaires / Sponsors',
  service:     'Services / Bien-être',
  other:       'Autre',
};

const EXPERIENCE_LABELS: Record<string, { label: string; emoji: string }> = {
  none:  { label: 'Premier festival',        emoji: '🐣' },
  '1-3': { label: 'Émergeant (1–3)',          emoji: '🌱' },
  '4-10':{ label: 'Confirmé (4–10)',          emoji: '🚀' },
  '11+': { label: 'Expert (+10 festivals)',   emoji: '🏆' },
};

const PRICE_LABELS: Record<string, string> = {
  budget:  '€  · Moins de 10€',
  medium:  '€€ · 10 à 20€',
  premium: '€€€ · 20 à 40€',
  luxury:  '€€€€ · Plus de 40€',
};

const REVENUE_LABELS: Record<string, string> = {
  '<500':      '< 500 €/j',
  '500-1500':  '500 – 1 500 €/j',
  '1500-5000': '1 500 – 5 000 €/j',
  '5000+':     '> 5 000 €/j',
};

// ─── Sub-components ───────────────────────────────────────────────────────────
const SectionTitle = ({ icon, title }: { icon: React.ReactNode; title: string }) => (
  <h2 className="text-xl font-black flex items-center gap-3 mb-6">
    <span className="text-[#FF5C00]">{icon}</span>
    {title}
  </h2>
);

const Spec = ({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) => (
  <div className="flex items-start gap-3">
    <span className="mt-0.5 text-gray-500 shrink-0">{icon}</span>
    <div>
      <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-0.5">{label}</p>
      <p className="text-sm font-semibold text-white">{value}</p>
    </div>
  </div>
);

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function StandDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const [stand, setStand] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [contacting, setContacting] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [hasConfirmedBooking, setHasConfirmedBooking] = useState(false);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'stands', id));
        if (snap.exists()) setStand({ id: snap.id, ...snap.data() });
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  // Vérifie si l'orga courant a une réservation confirmée avec ce stand.
  // On filtre standId + status en Firestore (2 champs = pas d'index composite requis),
  // puis on vérifie festivalOwnerId en JS pour éviter un index manquant.
  useEffect(() => {
    if (!id || !user) return;
    (async () => {
      try {
        const q = query(
          collection(db, 'applications'),
          where('standId', '==', id),
          where('status', '==', 'confirme')
        );
        const snap = await getDocs(q);
        setHasConfirmedBooking(
          snap.docs.some(d => d.data().festivalOwnerId === user.uid)
        );
      } catch {}
    })();
  }, [id, user]);

  const handleContact = async () => {
    if (!user) { toast.error('Connectez-vous pour contacter ce stand'); return; }
    if (!stand?.ownerId) return;
    if (stand.ownerId === user.uid) { toast.error('Vous ne pouvez pas vous contacter vous-même'); return; }

    setContacting(true);
    try {
      const q = query(collection(db, 'conversations'), where('participants', 'array-contains', user.uid), where('isGroup', '==', false));
      const snap = await getDocs(q);
      const existing = snap.docs.find(d => d.data().participants.includes(stand.ownerId));
      if (existing) {
        navigate('/messages', { state: { conversationId: existing.id } });
      } else {
        const ref = await addDoc(collection(db, 'conversations'), {
          name: stand.name,
          avatar: stand.logo || '',
          participants: [user.uid, stand.ownerId],
          participantDetails: {
            [user.uid]: { name: user.displayName || 'Organisateur', avatar: user.photoURL || '' },
            [stand.ownerId]: { name: stand.name, avatar: stand.logo || '' },
          },
          isGroup: false,
          lastMessage: 'Conversation démarrée',
          updatedAt: serverTimestamp(),
          unread: 0,
        });
        navigate('/messages', { state: { conversationId: ref.id } });
      }
    } catch (e) {
      toast.error("Erreur lors de l'ouverture de la messagerie");
    } finally {
      setContacting(false);
    }
  };

  const isOwner = user?.uid === stand?.ownerId;

  if (loading) return (
    <div className="min-h-screen bg-[#0d0e12] flex items-center justify-center">
      <div className="w-10 h-10 border-4 border-[#FF5C00]/30 border-t-[#FF5C00] rounded-full animate-spin" />
    </div>
  );

  if (!stand) return (
    <div className="min-h-screen bg-[#0d0e12] text-white flex flex-col items-center justify-center gap-4">
      <p className="text-xl font-bold">Stand introuvable</p>
      <button onClick={() => navigate(-1)} className="px-6 py-2 bg-[#FF5C00] text-white rounded-xl font-bold">Retour</button>
    </div>
  );

  const coverImage = stand.bannerUrl || stand.galleryUrls?.[0] || null;
  const exp = stand.experienceRange ? EXPERIENCE_LABELS[stand.experienceRange] : null;

  return (
    <div className="min-h-screen bg-[#0d0e12] text-white font-sans pb-28">

      {/* Bouton retour + modifier (si propriétaire) */}
      <div className="max-w-7xl mx-auto px-4 pt-4 pb-2 flex items-center justify-between">
        <button onClick={() => navigate(-1)} className="p-2 rounded-xl hover:bg-white/5 text-gray-400 hover:text-white transition-colors">
          <ChevronLeft className="w-5 h-5" />
        </button>
        {isOwner && (
          <button onClick={() => navigate('/my-stand')} className="text-xs font-bold bg-white/8 hover:bg-white/15 border border-white/10 px-4 py-2 rounded-xl transition-all">
            Modifier mon profil
          </button>
        )}
      </div>

      {/* ── Hero banner ── */}
      <div className="relative w-full h-[45vh] md:h-[55vh] overflow-hidden bg-[#121212]">
        {coverImage ? (
          <img src={coverImage} alt={stand.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#FF5C00]/20 via-[#121212] to-[#0d0e12]" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0d0e12] via-[#0d0e12]/60 to-transparent" />
      </div>

      {/* ── Identity block ── */}
      <div className="max-w-7xl mx-auto px-4 -mt-16 relative z-10">
        <div className="flex items-end gap-5 mb-5">
          {/* Logo */}
          {stand.logo ? (
            <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl border-4 border-[#0d0e12] overflow-hidden shrink-0 bg-black shadow-xl">
              <img src={stand.logo} alt="logo" className="w-full h-full object-cover" />
            </div>
          ) : (
            <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl border-4 border-[#0d0e12] shrink-0 bg-gradient-to-br from-[#FF5C00]/30 to-[#FF5C00]/10 flex items-center justify-center shadow-xl">
              <span className="text-3xl font-black text-[#FF5C00]">{stand.name?.charAt(0)?.toUpperCase()}</span>
            </div>
          )}

          <div className="flex-1 pb-1">
            {/* Badges top */}
            <div className="flex flex-wrap gap-2 mb-2">
              {stand.standType && (
                <span className="text-[10px] font-black uppercase tracking-widest bg-[#FF5C00] text-white px-3 py-1 rounded-full">
                  {STAND_TYPE_LABELS[stand.standType] || stand.standType}
                </span>
              )}
              {exp && (
                <span className="text-[10px] font-black uppercase tracking-widest text-[#FF5C00] bg-[#FF5C00]/10 border border-[#FF5C00]/20 px-3 py-1 rounded-full">
                  {exp.emoji} {exp.label}
                </span>
              )}
              {stand.priceRange && (
                <span className="text-[10px] font-black uppercase tracking-widest text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-3 py-1 rounded-full">
                  {PRICE_LABELS[stand.priceRange]}
                </span>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-3xl md:text-5xl font-black tracking-tight leading-tight">{stand.name}</h1>
              <RatingBadge rating={stand.rating} />
            </div>

            <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-400">
              {stand.region && <span className="flex items-center gap-1"><MapPin className="w-4 h-4 text-[#FF5C00]" />{stand.region}</span>}
              {stand.foundingYear > 1900 && <span className="flex items-center gap-1"><Calendar className="w-4 h-4 text-gray-500" />Depuis {stand.foundingYear}</span>}
              {stand.instagram && (
                <a href={`https://instagram.com/${stand.instagram.replace('@','')}`} target="_blank" rel="noreferrer"
                   className="flex items-center gap-1 hover:text-pink-400 transition-colors">
                  <Instagram className="w-4 h-4 text-pink-400" />{stand.instagram}
                </a>
              )}
              {stand.tiktok && (
                <a href={`https://tiktok.com/${stand.tiktok.startsWith('@') ? stand.tiktok : '@' + stand.tiktok}`} target="_blank" rel="noreferrer"
                   className="flex items-center gap-1 hover:text-white transition-colors">
                  <span className="text-xs font-black text-gray-500">TT</span>{stand.tiktok}
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Main layout ── */}
      <div className="max-w-7xl mx-auto px-4 pt-6">
        <div className="flex flex-col lg:flex-row gap-10">

          {/* ══ Left column ══════════════════════════════════════════════════ */}
          <div className="flex-1 space-y-10 min-w-0">

            {/* 1 — Histoire de la marque */}
            {(stand.brandStory || stand.description) && (
              <section className="bg-[#141414] rounded-3xl p-8 border border-white/5">
                <SectionTitle icon={<MessageSquare className="w-5 h-5" />} title="À propos" />
                {stand.brandStory && (
                  <p className="text-gray-300 leading-relaxed text-base mb-4 whitespace-pre-wrap">{stand.brandStory}</p>
                )}
                {stand.description && stand.description !== stand.brandStory && (
                  <p className="text-gray-400 leading-relaxed text-sm whitespace-pre-wrap">{stand.description}</p>
                )}
              </section>
            )}

            {/* 1bis — Références vérifiées (retours d'organisateurs post-festival) */}
            {Array.isArray(stand.references) && stand.references.length > 0 && (
              <section className="bg-emerald-500/[0.04] rounded-3xl p-8 border border-emerald-500/15">
                <SectionTitle icon={<BadgeCheck className="w-5 h-5" />} title={`Références vérifiées · ${stand.references.length}`} />
                <div className="space-y-4">
                  {[...stand.references]
                    .sort((a: any, b: any) => new Date(b.publishedAt || 0).getTime() - new Date(a.publishedAt || 0).getTime())
                    .map((ref: any, i: number) => (
                      <div key={ref.reviewId || i} className="bg-[#141414] rounded-2xl p-5 border border-white/5">
                        <p className="text-gray-200 leading-relaxed text-sm italic mb-3 whitespace-pre-wrap">« {ref.comment} »</p>
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <div className="flex items-center gap-2 text-xs">
                            <BadgeCheck className="w-4 h-4 text-emerald-400 shrink-0" />
                            <span className="text-emerald-400 font-bold">Organisateur vérifié</span>
                            <span className="text-gray-500">·</span>
                            <span className="text-gray-400 font-medium">{ref.festivalName}</span>
                            {ref.festivalDate && (
                              <span className="text-gray-600">
                                {new Date(ref.festivalDate).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                              </span>
                            )}
                          </div>
                          {typeof ref.rating === 'number' && (
                            <div className="flex items-center gap-1 text-[#FF5C00] text-xs font-black">
                              <Star className="w-3.5 h-3.5 fill-[#FF5C00]" /> {ref.rating.toFixed(1)}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              </section>
            )}

            {/* 2 — Galerie photos */}
            {stand.galleryUrls?.length > 0 && (
              <section>
                <SectionTitle icon={<LayoutGrid className="w-5 h-5" />} title={`Galerie · ${stand.galleryUrls.length} photo${stand.galleryUrls.length > 1 ? 's' : ''}`} />
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {stand.galleryUrls.map((url: string, i: number) => (
                    <div key={i} className={`overflow-hidden rounded-2xl bg-[#1a1a1a] border border-white/5 ${i === 0 ? 'md:col-span-2 md:row-span-2 aspect-[4/3]' : 'aspect-square'}`}>
                      <img src={url} alt={`Photo ${i + 1}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* 3 — Vidéo de présentation */}
            {stand.videoUrl && (
              <section className="bg-[#141414] rounded-3xl overflow-hidden border border-white/5">
                <div className="p-6 pb-0 flex items-center gap-3">
                  <Video className="w-5 h-5 text-purple-400" />
                  <h2 className="text-xl font-black">Vidéo de présentation</h2>
                  <span className="text-[10px] font-black uppercase tracking-widest bg-[#FF5C00]/20 text-[#FF5C00] px-2 py-0.5 rounded-full">Levier n°1</span>
                </div>
                <div className="relative mt-4">
                  <video
                    src={stand.videoUrl}
                    controls
                    className="w-full max-h-[420px] bg-black"
                    poster={stand.bannerUrl || undefined}
                  />
                </div>
              </section>
            )}

            {/* 4 — Expérience festivals */}
            {(stand.pastFestivals || stand.organizerTestimonial || stand.experienceRange) && (
              <section className="bg-[#141414] rounded-3xl p-8 border border-white/5">
                <SectionTitle icon={<Star className="w-5 h-5" />} title="Expérience festivals" />

                <div className="space-y-6">
                  {stand.pastFestivals && (
                    <div>
                      <p className="text-xs font-black uppercase tracking-widest text-gray-500 mb-3">Festivals réalisés</p>
                      <p className="text-gray-300 leading-relaxed whitespace-pre-wrap">{stand.pastFestivals}</p>
                    </div>
                  )}

                  {stand.organizerTestimonial && (
                    <div className="bg-yellow-500/5 border border-yellow-500/20 rounded-2xl p-6">
                      <div className="flex items-center gap-2 mb-3">
                        <MessageSquare className="w-4 h-4 text-yellow-400" />
                        <span className="text-xs font-black uppercase tracking-widest text-yellow-400">Témoignage organisateur</span>
                      </div>
                      <p className="text-gray-200 italic leading-relaxed text-sm">"{stand.organizerTestimonial}"</p>
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* 5 — CA & Potentiel commercial */}
            {(stand.revenueRange || stand.maxDailyVolume) && (
              <section className="bg-[#141414] rounded-3xl p-8 border border-white/5">
                <SectionTitle icon={<TrendingUp className="w-5 h-5" />} title="Potentiel commercial" />
                <p className="text-xs text-gray-500 mb-6">Données déclaratives et confidentielles — non visibles publiquement.</p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {stand.revenueRange && (
                    <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-2xl p-6">
                      <p className="text-xs font-black uppercase tracking-widest text-emerald-400 mb-2">CA moyen / festival</p>
                      <p className="text-2xl font-black text-white">{REVENUE_LABELS[stand.revenueRange] || stand.revenueRange}</p>
                    </div>
                  )}
                  {stand.maxDailyVolume && (
                    <div className="bg-blue-500/5 border border-blue-500/20 rounded-2xl p-6">
                      <p className="text-xs font-black uppercase tracking-widest text-blue-400 mb-2">Volume max / jour</p>
                      <p className="text-lg font-black text-white">{stand.maxDailyVolume}</p>
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* 6 — Dossier de presse */}
            {stand.pressKitUrl && (
              <section className="bg-indigo-500/5 rounded-3xl p-8 border border-indigo-500/20 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-indigo-500/20 rounded-2xl flex items-center justify-center border border-indigo-500/30">
                    <FileText className="w-6 h-6 text-indigo-400" />
                  </div>
                  <div>
                    <p className="font-black">Dossier de Présentation</p>
                    <p className="text-sm text-gray-400">Press kit, menu, informations complémentaires</p>
                  </div>
                </div>
                <a
                  href={stand.pressKitUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="shrink-0 inline-flex items-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white font-bold py-3 px-6 rounded-xl transition-all shadow-lg shadow-indigo-500/20"
                >
                  <FileText className="w-4 h-4" /> Consulter
                </a>
              </section>
            )}

          </div>

          {/* ══ Right sidebar (sticky) ════════════════════════════════════════ */}
          <div className="w-full lg:w-[340px] shrink-0">
            <div className="lg:sticky lg:top-20 space-y-6">

              {/* Contact card */}
              <div className="bg-[#141414] rounded-3xl p-7 border border-[#FF5C00]/20 shadow-[0_8px_30px_rgba(255,92,0,0.06)]">
                {/* Avatar */}
                <div className="flex items-center gap-4 mb-6 pb-6 border-b border-white/5">
                  {stand.logo ? (
                    <img src={stand.logo} alt="logo" className="w-14 h-14 rounded-2xl object-cover border border-white/10" />
                  ) : (
                    <div className="w-14 h-14 rounded-2xl bg-[#FF5C00]/20 flex items-center justify-center shrink-0">
                      <span className="text-xl font-black text-[#FF5C00]">{stand.name?.charAt(0)?.toUpperCase()}</span>
                    </div>
                  )}
                  <div>
                    <p className="font-black text-white">{stand.name}</p>
                    {stand.contactPerson && <p className="text-sm text-gray-400">{stand.contactPerson}</p>}
                  </div>
                </div>

                {/* Contact info — visible uniquement après réservation confirmée */}
                <div className="space-y-3 mb-6">
                  {isOwner || hasConfirmedBooking ? (
                    <>
                      {stand.email && (
                        <div className="flex items-center gap-3 text-sm text-gray-300">
                          <Mail className="w-4 h-4 text-gray-500 shrink-0" />
                          <a href={`mailto:${stand.email}`} className="truncate hover:text-white transition-colors">{stand.email}</a>
                        </div>
                      )}
                      {stand.phone && (
                        <div className="flex items-center gap-3 text-sm text-gray-300">
                          <Phone className="w-4 h-4 text-gray-500 shrink-0" />
                          <a href={`tel:${stand.phone}`} className="hover:text-white transition-colors">{stand.phone}</a>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="flex items-center gap-2 text-xs text-gray-500 bg-white/5 border border-white/10 rounded-xl px-4 py-3">
                      <span className="text-base">🔒</span>
                      <span>Coordonnées disponibles après confirmation de réservation</span>
                    </div>
                  )}
                </div>

                {!isOwner && (
                  <button
                    onClick={handleContact}
                    disabled={contacting}
                    className="w-full py-4 bg-[#FF5C00] hover:bg-[#FF5C00]/90 disabled:opacity-50 text-white font-black rounded-2xl transition-all flex items-center justify-center gap-2 shadow-lg shadow-[#FF5C00]/20 active:scale-95"
                  >
                    <Mail className="w-4 h-4" />
                    {contacting ? 'Connexion…' : 'Envoyer un message'}
                  </button>
                )}
              </div>

              {/* Fiche technique */}
              <div className="bg-[#141414] rounded-3xl p-7 border border-white/5">
                <h3 className="font-black text-white mb-6 flex items-center gap-2">
                  <Zap className="w-4 h-4 text-[#FF5C00]" /> Fiche Technique
                </h3>

                <div className="space-y-5">
                  {(stand.width > 0 || stand.depth > 0) && (
                    <Spec
                      icon={<LayoutGrid className="w-4 h-4" />}
                      label="Dimensions au sol"
                      value={`${stand.width || '?'}m × ${stand.depth || '?'}m`}
                    />
                  )}

                  <Spec
                    icon={<Zap className="w-4 h-4 text-yellow-400" />}
                    label="Électricité"
                    value={
                      stand.needsElectricity
                        ? <span className="text-yellow-400">{stand.powerKw ? `${stand.powerKw} kW requis` : 'Besoin électrique'}</span>
                        : <span className="text-gray-500">Aucun besoin</span>
                    }
                  />

                  <Spec
                    icon={<Droplets className="w-4 h-4 text-blue-400" />}
                    label="Accès eau"
                    value={
                      stand.waterNeeds
                        ? <span className="text-blue-400">Raccordement requis</span>
                        : <span className="text-gray-500">Aucun besoin</span>
                    }
                  />

                  {stand.hasVehicle !== undefined && (
                    <Spec
                      icon={<Truck className="w-4 h-4 text-purple-400" />}
                      label="Véhicule sur site"
                      value={stand.hasVehicle
                        ? <span className="text-purple-400">{stand.vehicleDetails || 'Oui'}</span>
                        : 'Non'
                      }
                    />
                  )}

                  {stand.staffBadges > 0 && (
                    <Spec
                      icon={<Users className="w-4 h-4" />}
                      label="Équipe / badges"
                      value={`${stand.staffBadges} personne${stand.staffBadges > 1 ? 's' : ''}`}
                    />
                  )}

                  {stand.furnitureNeeds && (
                    <Spec
                      icon={<Globe className="w-4 h-4" />}
                      label="Mobilier"
                      value={stand.furnitureNeeds}
                    />
                  )}

                  {stand.priceRange && (
                    <Spec
                      icon={<span className="text-emerald-400 font-black text-xs">€</span>}
                      label="Panier moyen"
                      value={<span className="text-emerald-400">{PRICE_LABELS[stand.priceRange]}</span>}
                    />
                  )}
                </div>
              </div>

              {/* Documents légaux */}
              <div className="bg-[#141414] rounded-3xl p-7 border border-white/5">
                <h3 className="font-black text-white mb-5 flex items-center gap-2">
                  <FileCheck className="w-4 h-4 text-emerald-400" /> Documents légaux
                </h3>

                <div className="space-y-3">
                  {[
                    { label: 'Assurance RC Pro', key: 'insuranceUrl' },
                    { label: 'Kbis / SIRET',     key: 'kbisUrl' },
                    { label: 'Agréments sanitaires', key: 'sanitaryUrl' },
                  ].map(({ label, key }) => {
                    const has = !!(stand as any)[key];
                    return (
                      <div key={key} className="flex items-center justify-between">
                        <span className="text-sm text-gray-400">{label}</span>
                        {has
                          ? <span className="flex items-center gap-1 text-xs font-bold text-emerald-400"><CheckCircle2 className="w-3.5 h-3.5" />Fourni</span>
                          : <span className="flex items-center gap-1 text-xs font-bold text-gray-600"><XCircle className="w-3.5 h-3.5" />Manquant</span>
                        }
                      </div>
                    );
                  })}
                </div>

                {stand.staffNames && (
                  <div className="mt-5 pt-5 border-t border-white/5">
                    <p className="text-xs font-black uppercase tracking-widest text-gray-500 mb-2">Noms de l'équipe</p>
                    <p className="text-xs text-gray-400 italic leading-relaxed">{stand.staffNames}</p>
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
